import axios from 'axios';
import Config from '../../../config';
import { OnboardingConfig, Priority } from '../onboardingIntelligence.types';

const oauthBase = (): string => Config.oauth ?? '';

export interface BulkPushResult {
    id: string;
    companyName: string;
    ok: boolean;
    error?: string;
}

export type BulkPushProgress = (done: number, total: number) => void;

export interface BulkPushItem {
    id: string;
    companyName: string;
}

export async function pushToLive(id: string): Promise<void> {
    await axios.post<void>(`${oauthBase()}/integration/golive/${id}`);
}

export async function updatePriority(id: string, priority: Priority): Promise<void> {
    await axios.post<void>(`${oauthBase()}/integration/OperatorUpdatePriority/${id}`, {
        Priority: priority,
    });
}

export async function getOnboardingConfig(): Promise<OnboardingConfig> {
    const { data } = await axios.get<OnboardingConfig>(
        `${oauthBase()}/integration/getconfig`
    );
    return data;
}

/**
 * Sequentially POST `/integration/golive/{id}` for each item. Mirrors the
 * existing `bulkAction` precedent at `onboarding_v2.js:411` — failures are
 * captured per row and the loop continues, returning one result per input
 * that the calling component can render in an inline alert. `onProgress`
 * fires after each row completes (success or fail) so the column can update
 * a progress strip.
 */
export async function bulkPushToLive(
    items: BulkPushItem[],
    onProgress?: BulkPushProgress
): Promise<BulkPushResult[]> {
    const out: BulkPushResult[] = [];
    for (let i = 0; i < items.length; i += 1) {
        const { id, companyName } = items[i];
        try {
            // eslint-disable-next-line no-await-in-loop
            await axios.post<void>(`${oauthBase()}/integration/golive/${id}`);
            out.push({ id, companyName, ok: true });
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err
                    ? String((err as { message: unknown }).message)
                    : 'Unknown error';
            out.push({ id, companyName, ok: false, error: message });
        }
        if (onProgress) onProgress(i + 1, items.length);
    }
    return out;
}
